<template>
  <transition name="ai-slide">
    <div v-if="isOpen" class="ai-panel">
      <div class="ai-header">
        <span class="ai-title">✦ AI</span>
        <div class="ai-mode-toggle">
          <button
            class="ai-mode-btn"
            :class="{ 'ai-mode-btn--active': mode === 'editor' }"
            @click="setMode('editor')"
          >
            Editor
          </button>
          <button
            class="ai-mode-btn"
            :class="{ 'ai-mode-btn--active': mode === 'info' }"
            @click="setMode('info')"
          >
            Info
          </button>
        </div>
        <button class="ai-close" @click="$emit('close')">×</button>
      </div>

      <div class="ai-messages" ref="messages">
        <div v-if="messages.length === 0" class="ai-empty">
          <p class="ai-empty-text">
            Ask me to suggest improvements for this {{ contextLabel }}.
          </p>
          <div class="ai-chips">
            <button
              v-for="chip in resolvedChips"
              :key="chip"
              class="ai-chip"
              @click="send(chip)"
            >
              {{ chip }}
            </button>
          </div>
        </div>

        <template v-for="(msg, idx) in messages">
          <div :key="'msg-' + idx" :class="['ai-msg', 'ai-msg--' + msg.role]">
            <div class="ai-bubble">{{ msg.text }}</div>
          </div>

          <div
            v-if="
              mode === 'editor' && msg.suggestions && msg.suggestions.length
            "
            :key="'sug-' + idx"
            class="ai-suggestions"
          >
            <div
              v-for="(s, si) in msg.suggestions"
              :key="si"
              class="sug-card"
              :class="{
                'sug-card--accepted': s.accepted,
                'sug-card--rejected': s.rejected
              }"
            >
              <div class="sug-field">{{ s.label }}</div>
              <div class="sug-diff">
                <div class="sug-diff-row">
                  <span class="sug-diff-label">Current</span>
                  <span class="sug-diff-val sug-diff-val--old">{{
                    stripHtml(s.current) || "(empty)"
                  }}</span>
                </div>
                <div class="sug-diff-row">
                  <span class="sug-diff-label">Suggested</span>
                  <span class="sug-diff-val sug-diff-val--new">{{
                    stripHtml(s.suggested)
                  }}</span>
                </div>
              </div>
              <div v-if="!s.accepted && !s.rejected" class="sug-actions">
                <button
                  class="sug-btn sug-btn--accept"
                  @click="accept(msg, si)"
                >
                  Accept
                </button>
                <button
                  class="sug-btn sug-btn--reject"
                  @click="reject(msg, si)"
                >
                  Reject
                </button>
              </div>
              <div v-else class="sug-status">
                <span v-if="s.accepted" class="sug-status--accepted"
                  >✓ Accepted</span
                >
                <span v-if="s.rejected" class="sug-status--rejected"
                  >✗ Skipped</span
                >
              </div>
            </div>
          </div>
        </template>

        <div v-if="loading" key="loading" class="ai-msg ai-msg--assistant">
          <div class="ai-bubble ai-bubble--typing">
            <span></span><span></span><span></span>
          </div>
        </div>
      </div>

      <div v-if="acceptedCount > 0" class="ai-apply-bar">
        <span class="ai-apply-count"
          >{{ acceptedCount }} change{{
            acceptedCount !== 1 ? "s" : ""
          }}
          accepted</span
        >
        <button class="ai-apply-btn" @click="applyChanges">
          Apply Changes
        </button>
      </div>

      <div class="ai-input-row">
        <textarea
          v-model="inputText"
          class="ai-input"
          placeholder="Ask for suggestions... (Ctrl+Enter to send)"
          rows="2"
          @keydown.enter.ctrl.prevent="send()"
        />
        <button
          class="ai-send"
          :disabled="loading || !inputText.trim()"
          @click="send()"
        >
          <span v-if="!loading">↑</span>
          <span v-else class="ai-send-loading">…</span>
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "AiSuggestionPanel",
  props: {
    isOpen: { type: Boolean, default: false },
    fetchSuggestions: { type: Function, required: true },
    contextLabel: { type: String, default: "item" },
    editorChips: { type: Array, default: null },
    infoChips: { type: Array, default: null }
  },
  data() {
    return {
      messages: [],
      history: [],
      inputText: "",
      loading: false,
      mode: "info"
    };
  },
  computed: {
    resolvedChips() {
      if (this.mode === "info") {
        return (
          this.infoChips || [
            "What vehicles is this compatible with?",
            "How many are needed per vehicle?",
            "What does this part do?"
          ]
        );
      }
      return (
        this.editorChips || [
          "Improve product titles",
          "Suggest better keywords",
          "Optimize for search"
        ]
      );
    },
    acceptedCount() {
      let count = 0;
      this.messages.forEach(msg => {
        if (msg.suggestions) {
          msg.suggestions.forEach(s => {
            if (s.accepted) count++;
          });
        }
      });
      return count;
    }
  },
  watch: {
    isOpen(val) {
      if (!val) {
        this.resetChat();
        this.mode = "info";
      }
    }
  },
  methods: {
    stripHtml(str) {
      if (!str) return "";
      return str
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    },

    setMode(m) {
      if (this.mode === m) return;
      this.mode = m;
      this.resetChat();
    },

    resetChat() {
      this.messages = [];
      this.history = [];
      this.inputText = "";
    },
    async send(text) {
      const prompt = text !== undefined ? text : this.inputText.trim();
      if (!prompt || this.loading) return;
      this.inputText = "";

      this.messages.push({ role: "user", text: prompt });
      this.loading = true;
      this.$nextTick(() => this.scrollBottom());

      try {
        // Pass history WITHOUT the current prompt — backend appends it
        // (and adds product context on the first call when history is empty)
        const result = await this.fetchSuggestions(
          prompt,
          this.history,
          this.mode
        );

        // Update history AFTER response so exchanges are always complete pairs
        this.history.push({ role: "user", content: prompt });

        const suggestions = (result.suggestions || []).map(s => ({
          ...s,
          accepted: false,
          rejected: false
        }));

        this.messages.push({
          role: "assistant",
          text: result.message || "Here are my suggestions:",
          suggestions
        });

        const rawContent =
          result._raw ||
          JSON.stringify({
            message: result.message,
            suggestions: result.suggestions
          });
        this.history.push({ role: "assistant", content: rawContent });
      } catch (e) {
        this.messages.push({
          role: "assistant",
          text:
            e.response?.data?.error ||
            e.message ||
            "Error getting suggestions.",
          suggestions: []
        });
      } finally {
        this.loading = false;
        this.$nextTick(() => this.scrollBottom());
      }
    },

    accept(msg, idx) {
      this.$set(msg.suggestions[idx], "accepted", true);
      this.$set(msg.suggestions[idx], "rejected", false);
    },

    reject(msg, idx) {
      this.$set(msg.suggestions[idx], "rejected", true);
      this.$set(msg.suggestions[idx], "accepted", false);
    },

    applyChanges() {
      const changes = [];
      this.messages.forEach(msg => {
        if (!msg.suggestions) return;
        msg.suggestions.forEach(s => {
          if (s.accepted) {
            changes.push({ field: s.field, value: s.suggested });
          }
        });
      });
      if (changes.length) {
        this.$emit("apply", changes);
      }
    },

    scrollBottom() {
      const el = this.$refs.messages;
      if (el) el.scrollTop = el.scrollHeight;
    }
  }
};
</script>

<style lang="scss" scoped>
.ai-slide-enter-active,
.ai-slide-leave-active {
  transition: transform 0.28s ease;
}
.ai-slide-enter,
.ai-slide-leave-to {
  transform: translateX(100%);
}

.ai-panel {
  position: fixed;
  top: 0;
  right: 0;
  width: 380px;
  height: 100vh;
  background: #0f0f1a;
  color: #e0e0e0;
  display: flex;
  flex-direction: column;
  z-index: 9999;
  box-shadow: -4px 0 30px rgba(0, 0, 0, 0.5);

  @media (max-width: 480px) {
    width: 100vw;
  }
}

.ai-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #1e1e2e;
  flex-shrink: 0;
}

.ai-title {
  font-size: 14px;
  font-weight: 600;
  color: #a78bfa;
  letter-spacing: 0.5px;
}

.ai-close {
  background: none;
  border: none;
  color: #666;
  font-size: 22px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  transition: color 0.15s;

  &:hover {
    color: #e0e0e0;
  }
}

.ai-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #2a2a3e;
    border-radius: 2px;
  }
}

.ai-empty {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px 0;
}

.ai-empty-text {
  font-size: 13px;
  color: #888;
  margin: 0;
  line-height: 1.5;
}

.ai-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ai-chip {
  background: #1e1e2e;
  border: 1px solid #2a2a3e;
  color: #a78bfa;
  font-size: 12px;
  padding: 6px 12px;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #2a2a3e;
    border-color: #a78bfa;
  }
}

.ai-msg {
  display: flex;

  &--user {
    justify-content: flex-end;
  }
  &--assistant {
    justify-content: flex-start;
  }
}

.ai-bubble {
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;

  .ai-msg--user & {
    background: #6c63ff;
    color: white;
    border-bottom-right-radius: 4px;
  }

  .ai-msg--assistant & {
    background: #1e1e2e;
    color: #d0d0e0;
    border-bottom-left-radius: 4px;
  }

  &--typing {
    display: flex;
    gap: 5px;
    align-items: center;
    padding: 14px 18px;

    span {
      width: 7px;
      height: 7px;
      background: #666;
      border-radius: 50%;
      animation: typing-dot 1.2s infinite;

      &:nth-child(2) {
        animation-delay: 0.2s;
      }
      &:nth-child(3) {
        animation-delay: 0.4s;
      }
    }
  }
}

@keyframes typing-dot {
  0%,
  60%,
  100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  30% {
    transform: translateY(-5px);
    opacity: 1;
  }
}

.ai-suggestions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-left: 4px;
}

.sug-card {
  background: #1a1a2e;
  border: 1px solid #2a2a3e;
  border-radius: 8px;
  padding: 12px;
  transition: border-color 0.15s;

  &--accepted {
    border-color: #22c55e;
    background: #0f2818;
  }

  &--rejected {
    border-color: #374151;
    opacity: 0.5;
  }
}

.sug-field {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #a78bfa;
  margin-bottom: 10px;
}

.sug-diff {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.sug-diff-row {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.sug-diff-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #555;
}

.sug-diff-val {
  font-size: 13px;
  line-height: 1.4;
  word-break: break-word;

  &--old {
    color: #888;
    text-decoration: line-through;
    text-decoration-color: #555;
  }

  &--new {
    color: #86efac;
  }
}

.sug-actions {
  display: flex;
  gap: 8px;
}

.sug-btn {
  flex: 1;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.15s;

  &--accept {
    background: #166534;
    color: #86efac;

    &:hover {
      background: #15803d;
    }
  }

  &--reject {
    background: #1e1e2e;
    color: #666;
    border: 1px solid #2a2a3e;

    &:hover {
      background: #2a2a3e;
      color: #999;
    }
  }
}

.sug-status {
  font-size: 12px;
  font-weight: 500;
}

.sug-status--accepted {
  color: #22c55e;
}
.sug-status--rejected {
  color: #555;
}

.ai-apply-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: #0d2218;
  border-top: 1px solid #166534;
  flex-shrink: 0;
}

.ai-apply-count {
  font-size: 12px;
  color: #86efac;
}

.ai-apply-btn {
  background: #166534;
  color: #86efac;
  border: none;
  border-radius: 5px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #15803d;
  }
}

.ai-input-row {
  display: flex;
  gap: 8px;
  padding: 12px;
  border-top: 1px solid #1e1e2e;
  flex-shrink: 0;
  align-items: flex-end;
}

.ai-input {
  flex: 1;
  background: #1e1e2e;
  border: 1px solid #2a2a3e;
  border-radius: 8px;
  color: #e0e0e0;
  font-size: 13px;
  padding: 8px 12px;
  resize: none;
  line-height: 1.4;
  font-family: inherit;
  transition: border-color 0.15s;

  &::placeholder {
    color: #444;
  }

  &:focus {
    outline: none;
    border-color: #6c63ff;
  }
}

.ai-send {
  width: 36px;
  height: 36px;
  background: #6c63ff;
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: #5b52e0;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.ai-send-loading {
  font-size: 20px;
  color: #ccc;
}

.ai-mode-toggle {
  display: flex;
  background: #1a1a2e;
  border-radius: 6px;
  padding: 2px;
  gap: 2px;
}

.ai-mode-btn {
  background: none;
  border: none;
  color: #666;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    color: #aaa;
  }

  &--active {
    background: #2a2a3e;
    color: #e0e0e0;
  }
}
</style>
